import type { TFunction } from "i18next";

/**
 * An export project from GET /api/ml-exports/projects/.
 *
 * Selecting a project fixes both `destination` and `task_kind` for the run —
 * the frontend sends `project_key` + `destination` (a project_key is NOT unique
 * on its own: the same key is offered for multiple destinations).
 */
export interface ExportProject {
  project_key: string;
  project_name: string;
  project_description?: string | null;
  /** Whether the project has been fully set up on the destination. */
  project_configured: boolean;
  /** Whether the project itself is enabled (selectable as an export target). */
  project_enabled: boolean;
  destination: string;
  destination_label: string;
  /** Whether the destination as a whole is enabled. */
  destination_enabled: boolean;
  task_kind: string;
  /**
   * The resolved provider-side target the images actually land in (e.g. the
   * Roboflow project slug), which can differ from `project_key` per
   * environment. The backend sends it only to staff, so the field's presence is
   * the only gate the frontend needs — never an `is_staff` check.
   */
  project_external_id?: string | null;
}

/** A project is a usable export target only when both it and its destination are on. */
export const isProjectSelectable = (p: ExportProject): boolean =>
  p.project_enabled && p.destination_enabled;

/**
 * Composite identity for a project row. `project_key` alone is not unique — the
 * same key is returned once per destination — so the destination must be part
 * of the key used for React lists, selection state, and lookups.
 */
export const projectUid = (p: Pick<ExportProject, "project_key" | "destination">): string =>
  `${p.project_key}::${p.destination}`;

export interface GetProjectsParams {
  destination?: string;
  task_kind?: string;
}

/** Task kinds the backend accepts, in the order the export switcher shows them. */
export const TASK_KIND_ORDER: readonly string[] = Object.freeze([
  "multi-label-classification",
  "semantic-segmentation",
  "object-detection",
  "instance-segmentation",
]);

/** The switcher opens here — the review grid it exports from is classification. */
export const DEFAULT_TASK_KIND = "multi-label-classification";

/** Task kinds are wire values; only their display text is translated. */
type TaskKindLabelKey =
  | "taskKinds.multiLabelClassification"
  | "taskKinds.semanticSegmentation"
  | "taskKinds.objectDetection"
  | "taskKinds.instanceSegmentation";

const TASK_KIND_LABEL_KEYS: Record<string, TaskKindLabelKey> = Object.freeze({
  "multi-label-classification": "taskKinds.multiLabelClassification",
  "semantic-segmentation": "taskKinds.semanticSegmentation",
  "object-detection": "taskKinds.objectDetection",
  "instance-segmentation": "taskKinds.instanceSegmentation",
});

/** Full label; kinds we don't know are Title-Cased from the key. */
export const taskKindLabel = (kind: string, t: TFunction<"mlExports">): string => {
  const key = TASK_KIND_LABEL_KEYS[kind];
  if (key) return t(key);
  return kind
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export interface TaskKindTab {
  kind: string;
  label: string;
  /** Number of (destination × project) targets — i.e. cards — in this kind. */
  count: number;
}

/**
 * Group the project list into switcher tabs. `/projects/` returns one row per
 * (destination × project), so the row count IS the card count. Known kinds keep
 * {@link TASK_KIND_ORDER}; anything else is appended, alphabetically.
 */
export const buildTaskKindTabs = (
  projects: ExportProject[] | undefined,
  t: TFunction<"mlExports">,
): TaskKindTab[] => {
  const counts = new Map<string, number>();
  for (const p of projects ?? []) {
    counts.set(p.task_kind, (counts.get(p.task_kind) ?? 0) + 1);
  }
  const known = TASK_KIND_ORDER.filter((kind) => counts.has(kind));
  const unknown = [...counts.keys()]
    .filter((kind) => !TASK_KIND_ORDER.includes(kind))
    .sort();
  return [...known, ...unknown].map((kind) => ({
    kind,
    label: taskKindLabel(kind, t),
    count: counts.get(kind) ?? 0,
  }));
};
